import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

interface PricingPlan {
  product_id?: string;
  stripeProductId?: string;
  name?: string;
  level?: number;
  active?: boolean;
  plan_type?: string;
  prices?: {
    month?: { value: number; stripePriceId: string };
    year?: { value: number; stripePriceId: string };
  };
}

interface PricingData {
  [key: string]: PricingPlan;
}

// Cache for pricing data
let pricingCache: PricingData | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch pricing data from Firebase
 */
export async function getPricingData(): Promise<PricingData | null> {
  // Check cache first
  if (pricingCache && Date.now() - cacheTimestamp < CACHE_TTL) {
    return pricingCache;
  }

  try {
    const docRef = doc(db, 'pricing', 'current');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as PricingData;
      pricingCache = data;
      cacheTimestamp = Date.now();
      return data;
    }
  } catch (error) {
    if (isFirebaseOfflineError(error)) {
      errorReporter.expected('Failed to fetch pricing data (client offline)', error, {
        feature: 'firebase',
        operation: 'getPricingData',
      });
    } else {
      // Critical: Pricing data is essential for subscription management
      errorReporter.critical('Failed to fetch pricing data', error, {
        feature: 'subscription',
        operation: 'getPricingData',
      });
    }
  }

  return null;
}

/**
 * Resolve the platform's current free-plan tier from a pricing document.
 *
 * The free plan is whichever tier is keyed under `free` AND active (production
 * keeps the current free plan there). Some environments (e.g. staging) leave an
 * old, inactive rate-based plan under `free` and host the current free plan under
 * a separate key (`freelifetime`, plan_type === 'lifetime_cap'); in that case we
 * pick the active lifetime-cap tier so callers don't resolve a stale free plan.
 * Falls back to the `free` key even if inactive as a last resort.
 */
function resolveFreePlanTier(pricing: PricingData | null): PricingPlan | null {
  if (!pricing) return null;

  // Preferred: the `free` key when it is the active free plan (production).
  if (pricing.free?.active) return pricing.free;

  // Otherwise the active lifetime-cap (free lifetime) plan, e.g. staging's `freelifetime`.
  const activeLifetimeFree = Object.values(pricing).find((plan) => plan.active && plan.plan_type === 'lifetime_cap');
  if (activeLifetimeFree) return activeLifetimeFree;

  // Last resort: the `free` key even if inactive (prior behavior).
  return pricing.free ?? null;
}

/**
 * Get the free plan product ID from Firebase
 */
export async function getFreePlanProductId(): Promise<string> {
  const pricing = await getPricingData();
  const freeTier = resolveFreePlanTier(pricing);

  if (freeTier) {
    // Return the product_id if set, otherwise stripeProductId
    return freeTier.product_id || freeTier.stripeProductId || 'free';
  }

  // Fallback - should rarely happen
  return 'prod_Su8J986mN10VZc';
}

/**
 * Check if a product ID represents a free plan
 * Only checks against the actual free plan in Firebase
 */
export async function isFreePlan(productId: string | null | undefined): Promise<boolean> {
  if (!productId) return false;

  // Get current free plan ID from Firebase
  const currentFreeId = await getFreePlanProductId();

  return productId === currentFreeId;
}

/**
 * Synchronous version using cached data (for UI components)
 * Only returns true if we have cached data and it matches
 */
export function isFreePlanSync(productId: string | null | undefined): boolean {
  if (!productId) return false;

  // Only check cached pricing data
  const freeTier = resolveFreePlanTier(pricingCache);
  if (freeTier) {
    const cachedFreeId = freeTier.product_id || freeTier.stripeProductId;
    return productId === cachedFreeId;
  }

  // If no cache, we can't determine - return false to be safe
  return false;
}

/**
 * Get plan details by product ID
 */
export async function getPlanByProductId(productId: string): Promise<PricingPlan | null> {
  const pricing = await getPricingData();

  if (!pricing) return null;

  // Search all plans for matching product_id or stripeProductId
  for (const plan of Object.values(pricing)) {
    if (plan.product_id === productId || plan.stripeProductId === productId) {
      return plan;
    }
  }

  return null;
}

/**
 * Get all available plans from Firebase
 */
export async function getAllPlans(): Promise<PricingData | null> {
  return getPricingData();
}

/**
 * Initialize pricing data cache on app startup
 */
export async function initializePricingCache(): Promise<void> {
  await getPricingData();
}
